import { useMemo, useRef, useState } from "react";

import { toastify } from "@helpers/toast";
import { getDebitNoteTotals, normalizeDebitNoteLine } from "../misc/helpers";
import { printDebitNote } from "../misc/print";

/**
 * Which debit note is open in the preview, and its vendor-payout lines.
 *
 * Only the id is held: the note itself is read back out of `notes`, so a status
 * change that refetches the list shows up in the open preview straight away.
 * @param notes the list the preview is opened from
 * @param fetchInvoices (noteId) => Promise<invoice[]>
 */
const useDebitNotePreview = ({ notes, fetchInvoices }) => {
    const [previewId, setPreviewId] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const documentRef = useRef(null);
    // Guards against a slow response for a note that has since been closed or
    // swapped for another.
    const requestRef = useRef(0);

    const previewNote = notes.find((note) => note._id === previewId) || null;
    const lines = useMemo(() => invoices.map(normalizeDebitNoteLine), [invoices]);
    const totals = useMemo(() => getDebitNoteTotals(lines, previewNote), [lines, previewNote]);

    const openPreview = async (note) => {
        const request = ++requestRef.current;
        setPreviewId(note._id);
        setInvoices([]);
        setInvoicesLoading(true);
        try {
            const result = await fetchInvoices(note._id);
            if (request === requestRef.current) setInvoices(result || []);
        } finally {
            if (request === requestRef.current) setInvoicesLoading(false);
        }
    };

    const closePreview = () => {
        requestRef.current++;
        setPreviewId(null);
        setInvoices([]);
        setInvoicesLoading(false);
    };

    const downloadPdf = () => {
        const opened = printDebitNote(documentRef.current, previewNote?.debit_note_number || "Debit note");
        if (!opened) toastify({ title: "Allow pop-ups for this site to download the PDF.", type: "error" });
    };

    return {
        previewNote,
        lines,
        totals,
        invoicesLoading,
        documentRef,
        openPreview,
        closePreview,
        downloadPdf
    };
};

export default useDebitNotePreview;
